import type { CaveEvent, Legend, LobbyAsset, LobbyDocument, TicketType } from "./api";

// Same-browser live preview transport (ADR 0134). The lobby editor broadcasts its
// current in-memory render bundle over a per-cave BroadcastChannel; the preview
// windows (opened via window.open on the same origin) subscribe and re-render
// instantly. A localStorage snapshot lets a window that opens cold (or after the
// editor reloaded) hydrate to the last known state. The draft never leaves the
// browser — no server round-trip, unlike the cross-device QR path (ADR 0134 part B).
// Every access is try/catch-wrapped (private windows / blocked storage / no
// BroadcastChannel) so a missing capability degrades to "no live updates", never a
// thrown error (mirrors prefs.ts / api.ts hardening).

/** Everything a preview needs to render the lobby exactly as the editor sees it. */
export interface LobbyPreviewBundle {
  caveId: string;
  doc: LobbyDocument;
  caveName: string;
  legend: Legend | null;
  assets: Record<string, LobbyAsset>;
  tickets: TicketType[];
  events: CaveEvent[];
}

export type LobbyPreviewMessage = { type: "state"; bundle: LobbyPreviewBundle } | { type: "hello" };

const channelName = (caveId: string) => `lobby-preview:${caveId}`;
const snapshotKey = (caveId: string) => `lobby-preview-snapshot:${caveId}`;

export interface LobbyPreviewChannel {
  /** Broadcast the current bundle to open preview windows + persist a snapshot. */
  post(bundle: LobbyPreviewBundle): void;
  /** Ask the editor (if open) to re-send the current bundle. */
  hello(): void;
  /** Subscribe to messages; returns an unsubscribe. */
  onMessage(cb: (m: LobbyPreviewMessage) => void): () => void;
  /** Last persisted bundle, for cold-open hydration (null when none/unavailable). */
  snapshot(): LobbyPreviewBundle | null;
  close(): void;
}

export function openLobbyPreviewChannel(caveId: string): LobbyPreviewChannel {
  let ch: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== "undefined") ch = new BroadcastChannel(channelName(caveId));
  } catch {
    ch = null;
  }
  return {
    post(bundle) {
      try {
        ch?.postMessage({ type: "state", bundle } satisfies LobbyPreviewMessage);
      } catch {
        /* channel closed / clone failed */
      }
      try {
        localStorage.setItem(snapshotKey(caveId), JSON.stringify(bundle));
      } catch {
        /* storage blocked / quota */
      }
    },
    hello() {
      try {
        ch?.postMessage({ type: "hello" } satisfies LobbyPreviewMessage);
      } catch {
        /* no channel */
      }
    },
    onMessage(cb) {
      if (!ch) return () => undefined;
      const handler = (e: MessageEvent) => {
        try {
          cb(e.data as LobbyPreviewMessage);
        } catch {
          /* malformed message */
        }
      };
      ch.addEventListener("message", handler);
      return () => ch?.removeEventListener("message", handler);
    },
    snapshot() {
      try {
        const s = localStorage.getItem(snapshotKey(caveId));
        return s ? (JSON.parse(s) as LobbyPreviewBundle) : null;
      } catch {
        return null;
      }
    },
    close() {
      try {
        ch?.close();
      } catch {
        /* already closed */
      }
    },
  };
}
